import React, { useState, useEffect } from 'react';
import FileViewerReact from 'react-file-viewer';
import printJS, { PrintTypes } from 'print-js';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { Button, Stack, Tooltip } from '@mui/material';

type IDocument = {
  uri: string;
  fileType: string;
};

const FileViewer = ({ file, name }: { file: Blob; name: string }) => {
  const [fileToView, setFileToView] = useState<IDocument>();

  useEffect(() => {
    async function convertBlobToFile() {
      if (file && file instanceof Blob && file.size > 0) {
        const fType = name.split('.').pop()?.toLowerCase() || '';
        const objectURL = URL.createObjectURL(file);
        setFileToView({ uri: objectURL, fileType: fType });
      } else {
        console.error('Invalid Blob or Blob is empty');
      }
    }

    convertBlobToFile();

    return () => {
      if (fileToView) {
        URL.revokeObjectURL(fileToView.uri);
      }
    };
  }, [file, name]);

  const handlePrint = () => {
    if (fileToView) {
      const type: PrintTypes | undefined = [
        'jpg',
        'jpeg',
        'png',
        'gif',
      ].includes(fileToView.fileType)
        ? 'image'
        : undefined;
      printJS(fileToView.uri, type);
    }
  };

  const handleDownload = () => {
    if (fileToView?.uri) {
      const link = document.createElement('a');
      link.href = fileToView.uri;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        alignItems="center"
        sx={{ padding: '8px 16px', marginBottom: '8px' }}>
        <Tooltip title="Download File">
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            Download
          </Button>
        </Tooltip>

        <Tooltip title="Print File">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            Print
          </Button>
        </Tooltip>
      </Stack>

      <div style={{ flex: 1, overflow: 'auto', textAlign: 'center', padding: '8px' }}>
        {fileToView &&
        fileToView.fileType &&
        imageTypes.includes(fileToView.fileType) ? (
          <img
            src={fileToView.uri}
            alt={name}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '4px' }}
          />
        ) : (
          <FileViewerReact
            style={{ height: '100%', width: '100%' }}
            fileType={fileToView?.fileType ?? ''}
            filePath={fileToView?.uri ?? ''}
            errorComponent={() => <div>Unable to preview file</div>}
            onError={(e: Error) => console.log(e)}
          />
        )}
      </div>
    </div>
  );
};

export default FileViewer;